import React, { useState, useEffect, useRef } from 'react';
import {
  neighborhoodService,
  uploadNeighborhoodImage
} from '../../services/neighborhoodService';
import { useAuth } from '../../controllers/auth';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  MapPin,
  Plus,
  Trash2,
  Edit2,
  Search,
  Upload,
  CheckCircle,
  XCircle,
  MoveUp,
  MoveDown,
  AlertCircle,
  Loader2,
  X,
  TrendingUp
} from 'lucide-react';

const CITIES = ['Varanasi, UP', 'Lucknow, UP', 'Ghazipur, UP'];

const Neighborhoods = () => {
  const { user, logAction } = useAuth();
  const [loading, setLoading] = useState(true);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Modal State for Add / Edit
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHood, setEditingHood] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: 'Varanasi, UP',
    image_url: '',
    price_trend: '',
    source_note: 'Indicative (Circle Rate Trend)',
    priority: 1,
    status: 'Active'
  });

  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [previewModalImg, setPreviewModalImg] = useState(null);

  // Confirmation Modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const fileInputRef = useRef(null);

  const loadData = async () => {
    setLoading(true);
    const { data } = await neighborhoodService.getNeighborhoods();
    setNeighborhoods(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenModal = (hood = null) => {
    setFormError('');
    setImageFile(null);

    if (hood) {
      setEditingHood(hood);
      setFormData({
        name: hood.name || '',
        city: hood.city || 'Varanasi, UP',
        image_url: hood.image_url || '',
        price_trend: hood.price_trend || '',
        source_note: hood.source_note || 'Indicative',
        priority: hood.priority || 1,
        status: hood.status || 'Active'
      });
    } else {
      setEditingHood(null);
      setFormData({
        name: '',
        city: 'Varanasi, UP',
        image_url: '',
        price_trend: '+10.0%',
        source_note: 'Indicative (Circle Rate Trend)',
        priority: neighborhoods.length + 1,
        status: 'Active'
      });
    }
    setIsModalOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFormError('Please select a valid image file (JPG, PNG, or WebP).');
      return;
    }

    setFormError('');
    setImageFile(file);
    // Instant local preview
    const preview = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, image_url: preview }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Please enter a locality name.');
      return;
    }

    if (!imageFile && !formData.image_url.trim()) {
      setFormError('Please upload an image or provide an image URL.');
      return;
    }

    setIsSubmitting(true);
    try {
      let finalImageUrl = formData.image_url;

      if (imageFile) {
        finalImageUrl = await uploadNeighborhoodImage(imageFile);
      }

      const payload = {
        ...formData,
        image_url: finalImageUrl
      };

      if (editingHood) {
        await neighborhoodService.updateNeighborhood(editingHood.id, payload);
        await logAction(user?.id, user?.full_name || 'Admin', 'UPDATE_NEIGHBORHOOD', editingHood.id, {
          name: payload.name,
          city: payload.city
        });
      } else {
        await neighborhoodService.createNeighborhood(payload);
        await logAction(user?.id, user?.full_name || 'Admin', 'CREATE_NEIGHBORHOOD', 'new', {
          name: payload.name,
          city: payload.city
        });
      }

      setIsModalOpen(false);
      await loadData();
    } catch (err) {
      console.error('Save failed:', err);
      setFormError(err.message || 'Failed to save neighborhood.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (hood) => {
    const updated = await neighborhoodService.toggleStatus(hood.id, hood.status);
    if (updated.data) {
      setNeighborhoods((prev) =>
        prev.map((h) => (h.id === hood.id ? { ...h, status: h.status === 'Active' ? 'Inactive' : 'Active' } : h))
      );
    }
  };

  const handleDelete = (hood) => {
    setConfirmTitle('Delete Investment Locality');
    setConfirmMessage(`Are you sure you want to delete "${hood.name}, ${hood.city}"? It will no longer appear on the website homepage.`);
    setPendingAction(() => async () => {
      await neighborhoodService.deleteNeighborhood(hood.id);
      await logAction(user?.id, user?.full_name || 'Admin', 'DELETE_NEIGHBORHOOD', hood.id, {
        name: hood.name
      });
      await loadData();
    });
    setConfirmOpen(true);
  };

  const handleMoveOrder = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= filteredList.length) return;

    const list = [...filteredList];
    const [moved] = list.splice(index, 1);
    list.splice(newIndex, 0, moved);

    setNeighborhoods(list);
    await neighborhoodService.updatePriorities(list);
  };

  const filteredList = neighborhoods.filter((h) => {
    const matchesSearch =
      (h.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (h.city || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = cityFilter === 'ALL' || (h.city || '').toLowerCase().includes(cityFilter.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || h.status === statusFilter;
    return matchesSearch && matchesCity && matchesStatus;
  });

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '28px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '12px',
            backgroundColor: 'rgba(168, 8, 12, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary)'
          }}>
            <MapPin size={22} />
          </div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Top Investment Neighborhoods
            </h1>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '2px 0 0' }}>
              Manage featured localities, local city photos, and YOY appreciation rates shown on the website homepage.
            </p>
          </div>
        </div>

        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
          style={{
            height: '44px',
            padding: '0 20px',
            borderRadius: '10px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(168, 8, 12, 0.28)'
          }}
        >
          <Plus size={18} />
          <span>Add Locality</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '14px',
        border: '1px solid var(--border-primary)',
        marginBottom: '24px'
      }}>
        <div style={{ position: 'relative', minWidth: '280px', flex: '1 1 300px' }}>
          <Search size={17} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text"
            placeholder="Search by locality or city name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              paddingLeft: '38px',
              paddingRight: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            style={{
              height: '40px',
              padding: '0 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="ALL">All Cities</option>
            <option value="Varanasi">Varanasi</option>
            <option value="Lucknow">Lucknow</option>
            <option value="Ghazipur">Ghazipur</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              height: '40px',
              padding: '0 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="ALL">All Status</option>
            <option value="Active">Active Only</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* List Table */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', backgroundColor: 'var(--bg-secondary)', borderRadius: '16px', border: '1px solid var(--border-primary)' }}>
          <Loader2 size={32} className="spin" style={{ margin: '0 auto 12px', color: 'var(--primary)' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading top localities...</p>
        </div>
      ) : filteredList.length === 0 ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', backgroundColor: 'var(--bg-secondary)', borderRadius: '16px', border: '1px dashed var(--border-primary)' }}>
          <MapPin size={40} style={{ color: 'var(--primary)', opacity: 0.5, margin: '0 auto 12px' }} />
          <h3 style={{ fontSize: '17px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
            No Neighborhoods Found
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 16px' }}>
            Try clearing filters or add a new investment locality.
          </p>
          <button onClick={() => handleOpenModal()} className="btn btn-primary" style={{ height: '40px', padding: '0 18px', borderRadius: '8px' }}>
            <Plus size={16} /> Add Locality
          </button>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '16px',
          border: '1px solid var(--border-primary)',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{
                  borderBottom: '1px solid var(--border-primary)',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: 'var(--text-secondary)'
                }}>
                  <th style={{ padding: '14px 16px', width: '70px', textAlign: 'center' }}>Order</th>
                  <th style={{ padding: '14px 16px', width: '130px' }}>Location Photo</th>
                  <th style={{ padding: '14px 16px' }}>Locality & City</th>
                  <th style={{ padding: '14px 16px', width: '150px' }}>YOY Appreciation</th>
                  <th style={{ padding: '14px 16px', width: '200px' }}>Source / Note</th>
                  <th style={{ padding: '14px 16px', width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '14px 20px', width: '120px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((hood, idx) => (
                  <tr key={hood.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    {/* Priority Order */}
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => handleMoveOrder(idx, 'up')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: idx === 0 ? 'var(--border-primary)' : 'var(--text-secondary)',
                            cursor: idx === 0 ? 'default' : 'pointer'
                          }}
                        >
                          <MoveUp size={14} />
                        </button>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
                          #{hood.priority || idx + 1}
                        </span>
                        <button
                          type="button"
                          disabled={idx === filteredList.length - 1}
                          onClick={() => handleMoveOrder(idx, 'down')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: idx === filteredList.length - 1 ? 'var(--border-primary)' : 'var(--text-secondary)',
                            cursor: idx === filteredList.length - 1 ? 'default' : 'pointer'
                          }}
                        >
                          <MoveDown size={14} />
                        </button>
                      </div>
                    </td>

                    {/* Image Preview */}
                    <td style={{ padding: '16px' }}>
                      <div
                        onClick={() => setPreviewModalImg(hood.image_url)}
                        style={{
                          width: '100px',
                          height: '65px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#000000',
                          cursor: 'pointer',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                        }}
                      >
                        <img
                          src={hood.image_url}
                          alt={hood.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </td>

                    {/* Locality & City */}
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '3px' }}>
                        {hood.name}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={13} style={{ color: 'var(--primary)' }} />
                        <span>{hood.city}</span>
                      </div>
                    </td>

                    {/* YOY Trend Badge */}
                    <td style={{ padding: '16px' }}>
                      {hood.price_trend ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 700,
                          backgroundColor: 'rgba(16, 185, 129, 0.12)',
                          color: '#059669',
                          border: '1px solid rgba(16, 185, 129, 0.25)'
                        }}>
                          <TrendingUp size={13} />
                          <span>{hood.price_trend} YOY</span>
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>None</span>
                      )}
                    </td>

                    {/* Source Note */}
                    <td style={{ padding: '16px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        {hood.source_note || 'Indicative'}
                      </span>
                    </td>

                    {/* Status Toggle */}
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(hood)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: hood.status === 'Active' ? '#059669' : '#94a3b8'
                        }}
                      >
                        {hood.status === 'Active' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                        <span>{hood.status}</span>
                      </button>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={() => handleOpenModal(hood)}
                          className="btn-icon"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'var(--bg-primary)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Edit"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(hood)}
                          className="btn-icon"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid rgba(220, 38, 38, 0.2)',
                            backgroundColor: 'rgba(220, 38, 38, 0.05)',
                            color: '#dc2626',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ADD / EDIT NEIGHBORHOOD MODAL                                             */}
      {/* ========================================================================= */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#FFFEFE',
            color: '#0f172a',
            borderRadius: '18px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
            border: '1px solid var(--border-primary)'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                  {editingHood ? 'Edit Investment Locality' : 'Add Top Locality'}
                </h3>
                <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Locality photo, city tag, and appreciation details for homepage showcase.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              {formError && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  border: '1px solid rgba(220, 38, 38, 0.3)',
                  color: '#b91c1c',
                  fontSize: '13px',
                  marginBottom: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={16} />
                  <span>{formError}</span>
                </div>
              )}

              {/* Locality Name */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                  Locality Name <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Assi Ghat or Gomti Nagar"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    height: '44px',
                    padding: '0 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    color: '#0f172a',
                    backgroundColor: '#ffffff'
                  }}
                />
              </div>

              {/* City Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                  City <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <input
                  type="text"
                  list="city-options"
                  required
                  placeholder="e.g. Varanasi, UP"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  style={{
                    width: '100%',
                    height: '44px',
                    padding: '0 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    color: '#0f172a',
                    backgroundColor: '#ffffff'
                  }}
                />
                <datalist id="city-options">
                  {CITIES.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>

              {/* Image Upload / URL */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                  Locality Photo <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '8px' }}>
                  {formData.image_url ? (
                    <div style={{
                      width: '110px',
                      height: '70px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #cbd5e1',
                      backgroundColor: '#000000',
                      flexShrink: 0
                    }}>
                      <img src={formData.image_url} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ) : null}
                  <div style={{ flex: 1 }}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn"
                      style={{
                        height: '40px',
                        padding: '0 16px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#475569',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Upload size={15} /> Upload Photo (Auto-compressed to 1200px)
                    </button>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                      Select scenic photo of the landmark or locality. Max 1200px width.
                    </div>
                  </div>
                </div>

                {/* Direct URL input fallback */}
                <input
                  type="text"
                  placeholder="Or paste direct image URL (e.g. /images/neighborhoods/assi-ghat.jpg)"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  style={{
                    width: '100%',
                    height: '38px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '12px',
                    color: '#0f172a',
                    backgroundColor: '#ffffff'
                  }}
                />
              </div>

              {/* 2-Column: YOY Trend & Source Note */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    YOY Growth % (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. +15.1% (leave blank if none)"
                    value={formData.price_trend}
                    onChange={(e) => setFormData({ ...formData, price_trend: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Source / Disclaimer Note
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Indicative or Source: Registry Index"
                    value={formData.source_note}
                    onChange={(e) => setFormData({ ...formData, source_note: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  />
                </div>
              </div>

              {/* Order & Status */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Display Order (Priority)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Publish Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  >
                    <option value="Active">Active (Shown on Homepage)</option>
                    <option value="Inactive">Inactive (Hidden)</option>
                  </select>
                </div>
              </div>

              {/* Submit Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '18px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                  style={{
                    height: '44px',
                    padding: '0 18px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                  style={{
                    height: '44px',
                    padding: '0 24px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      <span>Saving Locality...</span>
                    </>
                  ) : (
                    <span>{editingHood ? 'Update Locality' : 'Save Locality'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Full Preview Modal */}
      {previewModalImg && (
        <div
          onClick={() => setPreviewModalImg(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div style={{ maxWidth: '900px', width: '100%', maxHeight: '80vh', position: 'relative' }}>
            <img src={previewModalImg} alt="Preview" style={{ width: '100%', height: 'auto', borderRadius: '12px' }} />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        type="danger"
        confirmText="Delete Locality"
        onConfirm={async () => {
          if (pendingAction) await pendingAction();
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default Neighborhoods;
